import React from 'react';
import * as R from 'ramda';
import { gql } from '@apollo/client';
import DateTimeField from 'components/DateTimeField';
import {
  Datagrid,
  TextField,
  FunctionField,
  BooleanField,
  ArrayField,
  SingleFieldList,
  Link,
} from 'react-admin';
import EnumSelectInput from 'components/Enum/EnumSelectInput';
import { IdFilter, SearchFilter } from 'components/Filter';
import LinkField from 'components/LinkField';
import List from 'components/List';
import { DisplayHelpers } from 'utils/helpers';
import IdField from 'components/IdField';
import PositionInput from 'components/PositionInput';
import DateFilter from 'components/Filter/DateFilter';
import { SalesAssociateField } from 'resources/assignments/SalesAssociateField';
import { PermanentPlacementStatus } from 'components/Enum/enums';

type Filters = { [key in PermanentPlacementStatus]: boolean };

const defaultPlacementStatusFilters: Filters = {
  [PermanentPlacementStatus.open]: false,
  [PermanentPlacementStatus.offer_sent]: false,
  [PermanentPlacementStatus.hired]: false,
  [PermanentPlacementStatus.canceled]: false,
};

const parseStatusFilter = (value: PermanentPlacementStatus) =>
  value
    ? {
        ...defaultPlacementStatusFilters,
        [value]: true,
      }
    : undefined;

const formatStatusFilter = (value?: Filters): PermanentPlacementStatus | undefined => {
  if (!!value) {
    const entries = Object.entries(value) as [PermanentPlacementStatus, boolean][];

    return entries.find(([_filterName, isOn]) => isOn)?.[0];
  }
};

const PermanentPlacementsFilters = [
  <SearchFilter alwaysOn />,
  <IdFilter />,
  <IdFilter
    label="Location ID"
    source="search.location_id"
    alwaysOn
    data-testid="permanent-placement_filters-location_id"
  />,
  <PositionInput alwaysOn multiple source="search.position_ids" />,
  <EnumSelectInput
    label="Permanent Placement Status"
    emptyText="All"
    alwaysOn
    source="search.permanent_placement_status"
    parse={parseStatusFilter}
    format={formatStatusFilter}
  />,
  <DateFilter label="Starts Date" source="search.starts_date" />,
  <DateFilter label="Ends Date" source="search.ends_date" />,
];

const PermanentPlacementsList = () => (
  <List
    exporter={false}
    filters={PermanentPlacementsFilters}
    data-testid="permanent-placement-list"
  >
    <Datagrid bulkActionButtons={false} data-testid="permanent-placement-table">
      <IdField resource="PermanentPlacement" />
      <TextField label="Status" source="status" data-testid="permanent-placement_table-status" />
      <BooleanField
        label="Applications Paused"
        source="paused"
        data-testid="permanent-placement_table-paused"
      />
      <LinkField label="Professional ID" source="professional_id" />
      <ArrayField sortable={false} label="Location ID(s)" source="locations">
        <SingleFieldList linkType={false}>
          <LinkField source="id" />
        </SingleFieldList>
      </ArrayField>
      <LinkField label="Company ID" source="company_id" />
      <DateTimeField label="Starts Date" format="MM/dd/yyyy" source="starts_date" />
      <FunctionField
        label="Applications"
        render={(record: any) =>
          record.assignment_request_applied_count && (
            <LinkField to={`/Assignment/${record.id}/show/requests`}>
              {record.assignment_request_applied_count}
            </LinkField>
          )
        }
        data-testid="permanent-placement_table-applications"
      />
      <FunctionField
        label="Position"
        render={DisplayHelpers.professional.positionNames}
        data-testid="permanent-placement_table-position"
      />
      <FunctionField
        label="States"
        render={(v) => R.uniq(R.map((l) => l.state?.name, v.locations)).join('\n')}
        data-testid="permanent-placement_table-states"
      />
      <FunctionField
        label="Markets"
        render={(v) => R.uniq(R.map((l) => l.market?.display_name, v.locations)).join('\n')}
        data-testid="permanent-placement_table-markets"
      />
      <FunctionField
        label="Account Manager"
        render={(r) =>
          r.administrator &&
          r.administrator.account && (
            <Link to={`/Administrator/${r.administrator_id}/show`}>
              {r.administrator.account.name}
            </Link>
          )
        }
        data-testid="permanent-placement_table-account_manager"
      />
      <SalesAssociateField label="Sales Associate" />
      <FunctionField
        label="Clinical Account Manager"
        render={(r) =>
          r.recruiter &&
          r.recruiter.account && (
            <Link to={`/Administrator/${r.recruiter_id}/show`}>{r.recruiter.account.name}</Link>
          )
        }
        data-testid="permanent-placement_table-clinical_account_manager"
      />
      <TextField
        label="Number of Openings"
        source="number_of_openings"
        data-testid="permanent-placement_table-number_of_openings"
      />
    </Datagrid>
  </List>
);

PermanentPlacementsList.query = gql`
  query assignments($input: AssignmentQueryInput) {
    _permanentPlacementsMeta(input: $input) {
      count
    }
    permanentPlacements(input: $input) {
      id
      status
      company {
        id
        msp {
          id
          label
        }
        health_system {
          id
          label
        }
        vms_type {
          id
          label
          name
        }
      }
      order_number
      on_hold
      paused
      professional_id
      professional {
        id
        account {
          id
          first_name
          last_name
        }
      }
      locations {
        id
        timezone_lookup
        name
        market {
          id
          display_name
        }
        state {
          id
          name
        }
      }
      company_id
      starts_date
      ends_date
      number_of_shifts_per_week
      hours_per_shift
      charge_base_hourly_rate_cents
      charge_per_week_cents
      charge_blended_hourly_rate_cents
      assignment_request_applied_count
      assignment_profile {
        charge_base_hourly_rate_cents
        charge_per_week_cents
        charge_blended_hourly_rate_cents
      }
      assignment_requests {
        id
        phone_screening_request {
          id
          assignment_request {
            id
            professional_id
          }
          confirmed_phone_screening_time
          masked_professional_phone_number
          status
        }
      }
      positions {
        id
        display_name
      }
      administrator_id
      administrator {
        id
        account {
          id
          name
        }
      }
      recruiter_id
      recruiter {
        id
        account {
          id
          name
        }
      }
      number_of_openings
      original_assignment_id
      primary_location {
        id
        timezone_lookup
        covid_requirement
      }
      starts_time
      ends_time
      onboarding_due_date
      schedule_type
      is_w2
      pay_per_week_cents
      one_time_bonus_burden_cents
      flexible_starts_date
      number_of_weeks
      assignment_locations {
        primary_location
        location {
          id
          sales_associate {
            id
            account {
              name
            }
          }
        }
      }
    }
  }
`;

export default PermanentPlacementsList;
